import keycloak from 'auth/keycloak';
import { migrate } from 'data-migrations/migration';
import type { UnitReducerInterface } from 'store/units/reducer';
import { initialConnectionState, initialUnitState } from 'store/units/reducer';
import { UnitTypes } from 'unitData/types';
import { initialState as initialSettingState } from 'store/settings/reducer';
import { initialState as initialDocumentMetadataState } from 'store/documentMetadata/reducer';
import type { ProjectDataInterface } from 'store/documentMetadata/types';
import { initialState as initialFileState } from 'store/fileState/reducer';
import dayjs from 'dayjs';
import type { FileState, WorkflowMetadata } from 'store/storageunits/types';
import { getLatestDataVersion } from 'data-migrations/service/version-service';
import { DATA_VERSION } from 'data-migrations/dataVersions';
import { saveAs } from 'file-saver';
import { decode, encode } from 'js-base64';
import { FileStatus } from 'store/fileState/types';

const drawingSheetExists = (units: UnitReducerInterface['units']) =>
  units.some((unit) => unit.type === UnitTypes.PRINT_WINDOW);

/**
 * Creates state for a new file
 * @param fileName name of the file
 * @param flowsheetMetadata optional metadata for internal users
 * @returns the new state
 */
export const createFileState = (
  fileName: string,
  flowsheetMetadata?: WorkflowMetadata,
): FileState => {
  return {
    unitState: initialUnitState,
    connectionState: initialConnectionState,
    settingState: {
      ...initialSettingState,
      fileName,
    },
    documentMetadata: {
      ...initialDocumentMetadataState,
      projectData: {
        ...initialDocumentMetadataState.projectData,
        drawnBy: keycloak.idTokenParsed?.name ?? '',
        userName: keycloak.idTokenParsed?.name ?? '',
        currentDate: dayjs().format('YYYY-MM-DD'),
      },
      flowsheetMetadata,
    },
    fileState: {
      ...initialFileState,
    },
  };
};

/**
 * Parses state from a Base64 encoded string
 * @param fileName name of the file
 * @param fileContent Base64 encoded string
 * @param clearApprovedStatus whether to clear approval status
 * @param flowsheetMetadata optional metadata from API response (takes precedence over file content)
 * @returns the parsed state
 */
export const importFileState = (
  fileName: string,
  fileContent: string,
  clearApprovedStatus: boolean,
  flowsheetMetadata?: WorkflowMetadata,
): FileState => {
  const parsedState = JSON.parse(decode(fileContent));
  const originalVersion = parsedState.dataVersion;
  const migratedState = migrate(parsedState);

  const { connections } = migratedState.state.connectionState;
  const {
    units,
    ports,
    panels,
    groups,
    simulationResults,
    chamberAnalysisResults,
  } = migratedState.state.unitState;

  // Check if new structure exists (files saved with new format)
  const migratedStateAny = migratedState.state as unknown as Record<
    string,
    Record<string, unknown> | undefined
  >;
  const hasNewStructure =
    migratedStateAny.documentMetadata ?? migratedStateAny.fileState;

  // Extract from new structure if available, otherwise fall back to old settings structure
  const projectData = hasNewStructure
    ? migratedStateAny.documentMetadata?.projectData
    : migratedStateAny.settingState?.projectData;

  const fileStatus = hasNewStructure
    ? migratedStateAny.fileState?.fileStatus
    : migratedStateAny.settingState?.fileStatus;

  const fileHasBeenEdited = hasNewStructure
    ? migratedStateAny.fileState?.fileHasBeenEdited
    : migratedStateAny.settingState?.fileHasBeenEdited;

  const approvedBy = (projectData as { approvedBy?: string } | undefined)
    ?.approvedBy;
  const { displayDraft } = migratedState.state.settingState.printProperties;

  const {
    unitDisplay,
    printProperties,
    plantDisplay,
    metricType,
    oldMetricType,
    screenCalcMethod,
    canvasBodyFontSize,
    autoSimulate,
  } = migratedState.state.settingState;

  // Merge flowsheetMetadata from all sources
  // Priority: API > FileData (new) > FileData (legacy)
  // Note: riskAssessmentSimulationSettings is new and won't be in legacy,
  // but other flowsheetMetadata fields (application, ai, wi) may be in legacy location
  const fileMetadataNew = migratedStateAny.documentMetadata?.flowsheetMetadata;
  const fileMetadataLegacy = migratedStateAny.settingState?.flowsheetMetadata;

  const finalMetadata = {
    ...(fileMetadataLegacy ?? {}),
    ...(fileMetadataNew ?? {}),
    ...(flowsheetMetadata ?? {}),
  };

  return {
    unitState: {
      ...initialUnitState,
      groups,
      units: clearApprovedStatus ? displayHiddenNonMajorWarnings(units) : units,
      ports,
      panels,
      simulationResults: simulationResults || {},
      chamberAnalysisResults: chamberAnalysisResults || {},
    },
    connectionState: {
      ...initialConnectionState,
      connections,
    },
    settingState: {
      ...initialSettingState,
      unitDisplay: {
        ...initialSettingState.unitDisplay,
        ...unitDisplay,
        ...(originalVersion < DATA_VERSION.DATA_VERSION27 && {
          showRiskLevel: true,
        }),
      },
      printProperties: {
        ...printProperties,
        displayDraft: clearApprovedStatus ? true : displayDraft,
      },
      plantDisplay: {
        ...initialSettingState.plantDisplay,
        ...plantDisplay,
        drawingSheet: drawingSheetExists(units),
      },
      metricType,
      oldMetricType,
      screenCalcMethod,
      fileName: fileName.replace('.ngpd', ''),
      canvasBodyFontSize,
      autoSimulate: autoSimulate ?? initialSettingState.autoSimulate,
    },
    documentMetadata: {
      projectData: {
        ...((projectData as ProjectDataInterface | undefined) ??
          initialDocumentMetadataState.projectData),
        userName: keycloak.idTokenParsed?.name ?? '',
        approvedBy: clearApprovedStatus ? '' : (approvedBy ?? ''),
      },
      flowsheetMetadata: finalMetadata,
    },
    fileState: {
      fileStatus: (fileStatus as FileStatus | undefined) ?? FileStatus.LOADING,
      fileHasBeenEdited: (fileHasBeenEdited as boolean | undefined) ?? false,
    },
  };
};

const displayHiddenNonMajorWarnings = (units: UnitReducerInterface['units']) =>
  units.map((unit) => {
    if (unit.layout && 'hideNonMajorWarnings' in unit.layout) {
      return {
        ...unit,
        layout: {
          ...unit.layout,
          hideNonMajorWarnings: false,
        },
      };
    }
    return unit;
  });

/**
 * Exports file state to a Base64 encoded string
 * @param currentState the current unit, connection, setting, documentMetadata and fileState
 * @returns Base64 encoded string
 */
export const exportFileState = ({
  unitState,
  connectionState,
  settingState,
  documentMetadata,
  fileState,
  clearApprovedStatus,
}: FileState): string => {
  const { units } = unitState;

  // For backward compatibility, merge documentMetadata and fileState back into settingState
  // This ensures old versions of the app can still read files saved with the new structure
  const legacySettingState = {
    ...settingState,
    projectData: {
      ...(documentMetadata?.projectData ??
        initialDocumentMetadataState.projectData),
      approvedBy: clearApprovedStatus
        ? ''
        : documentMetadata?.projectData.approvedBy,
    },
    flowsheetMetadata: documentMetadata?.flowsheetMetadata,
    fileStatus: fileState?.fileStatus ?? FileStatus.LOADING,
    fileHasBeenEdited: clearApprovedStatus
      ? false
      : fileState?.fileHasBeenEdited,
    simulating: false, // UI state, always reset on save
    showApproveFileModal: false, // UI state, always reset on save
    skipsDiagramUpdate: false, // UI state, always reset on save
  };

  // Update printProperties.displayDraft
  if (clearApprovedStatus) {
    legacySettingState.printProperties = {
      ...legacySettingState.printProperties,
      displayDraft: true,
    };
  }

  const stateToExport: FileState = {
    unitState: {
      ...unitState,
      units: clearApprovedStatus ? displayHiddenNonMajorWarnings(units) : units,
    },
    connectionState,
    settingState: legacySettingState,
    // Include new structure for forward compatibility
    documentMetadata: {
      projectData: {
        ...(documentMetadata?.projectData ??
          initialDocumentMetadataState.projectData),
        approvedBy: clearApprovedStatus
          ? ''
          : (documentMetadata?.projectData.approvedBy ?? ''),
      },
      flowsheetMetadata: documentMetadata?.flowsheetMetadata,
    },
    fileState: {
      fileStatus: fileState?.fileStatus ?? FileStatus.LOADING,
      fileHasBeenEdited: clearApprovedStatus
        ? false
        : (fileState?.fileHasBeenEdited ?? false),
    },
    dataVersion: getLatestDataVersion(),
  };

  return encode(JSON.stringify(stateToExport));
};

export const downloadFile = (
  fileName: string,
  fileState?: FileState,
  fileData?: string,
) => {
  let downloadState = {} as FileState;
  if (fileData) {
    downloadState = importFileState(fileName, fileData, true);
  } else if (fileState) {
    downloadState = fileState;
  }

  const {
    unitState,
    connectionState,
    settingState,
    documentMetadata,
    fileState: currentFileState,
  } = downloadState;
  const fileContent = exportFileState({
    unitState,
    connectionState,
    settingState,
    documentMetadata,
    fileState: currentFileState,
    clearApprovedStatus: true,
  });

  const name = addFileEndingIfMissing(fileName);
  const type = 'application/plant-designer';

  const blob = new Blob([fileContent], { type });
  const file = new File([blob], name);
  saveAs(file);
};

export const addFileEndingIfMissing = (fileName: string) =>
  !fileName.endsWith('.ngpd') ? `${fileName}.ngpd` : fileName;
